"use client";

import { useEffect } from "react";

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

const LOG_ERROR_PATH = "/app-router/log-error";
// Mirrors report-app-router-error.ts's MAX_FIELD_LENGTH client-side cap.
const MAX_REPORT_FIELD = 4000;

function capField(value: unknown): unknown {
  return typeof value === "string" ? value.slice(0, MAX_REPORT_FIELD) : value;
}

// Inlined copy of report-app-router-error.ts's own stripOrigin/detectAssetPrefix
// (this release branch predates the shared utils/asset-prefix.ts extraction on
// devdeploy/next — that dedup is deferred to the follow-up backport, so this
// duplicates the release branch's CURRENT local implementation rather than
// importing a module that doesn't exist here yet).
let cachedPrefix: string | null = null;

function stripOrigin(value: string): string {
  return value.replace(/^(?:https?:)?\/\/[^/]+/, "");
}

function stripTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, "");
}

function toSameOriginPath(prefix: string): string {
  return /^\/(?!\/)[\w./~-]*$/.test(prefix) ? prefix : "";
}

function detectAssetPrefix(): string {
  if (cachedPrefix !== null) {
    return cachedPrefix;
  }
  let prefix = "";
  try {
    const nextData =
      // eslint-disable-next-line no-underscore-dangle
      typeof window !== "undefined" ? (window as any).__NEXT_DATA__ : undefined;
    const fromData = nextData && nextData.assetPrefix;
    if (typeof fromData === "string" && fromData) {
      prefix = stripTrailingSlashes(stripOrigin(fromData));
    } else if (typeof document !== "undefined") {
      const el =
        document.querySelector('script[src*="/_next/"]') ||
        document.querySelector('link[href*="/_next/"]');
      const assetUrl =
        (el && (el.getAttribute("src") || el.getAttribute("href"))) || "";
      const marker = assetUrl.indexOf("/_next/");
      if (marker > 0) {
        prefix = stripTrailingSlashes(stripOrigin(assetUrl.slice(0, marker)));
      }
    }
  } catch {
    prefix = "";
  }
  cachedPrefix = toSameOriginPath(prefix);
  return cachedPrefix;
}

// Dependency-free duplicate of
// app/components/error-boundary/report-app-router-error.ts's reporting logic
// — deliberately NOT imported from there. That module pulls in
// originalConsoleError from utils/suppressConsoleMethods.ts, which
// unconditionally imports @directv-digital/dds.services.cookie-service —
// exactly the kind of provider/DDS dependency this file's own comment below
// forbids, since a failure in THAT import could cascade into this
// last-resort fallback.
//
// Trade-off accepted: without originalConsoleError this can't guarantee
// console.error survives prod console suppression on the client — but the
// server-side beacon below is what actually guarantees the error reaches
// stdout (WAT-10904 AC3), unconditionally, regardless of client console
// state. Best-effort and swallows its own failures, matching every other
// boundary's reporter.
function reportGlobalError(error: GlobalErrorProps["error"]): void {
  // Unconditional — NOT gated on NODE_ENV like the pattern this replaced, so
  // it fires in real production too (WAT-10904 AC4: error boundary logging
  // must not be silenced).
  console.error("[AppRouterGlobalError]", {
    message: error?.message,
    digest: error?.digest,
    name: error?.name
  });

  try {
    const endpoint = `${detectAssetPrefix()}${LOG_ERROR_PATH}`;
    const payload = JSON.stringify({
      scope: "AppRouterGlobalError",
      message: capField(error?.message),
      name: error?.name,
      stack: capField(error?.stack),
      digest: error?.digest
    });

    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const queued = navigator.sendBeacon(
        endpoint,
        new Blob([payload], { type: "application/json" })
      );
      if (queued) {
        return;
      }
    }

    if (typeof fetch === "function") {
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true
      }).catch(() => { /* best-effort — see comment above */ });
    }
  } catch { /* best-effort — see comment above */ }
}

// global-error.tsx replaces the root layout when an error escapes app/error.tsx
// (typically a render failure inside app/layout.tsx itself). It must include
// its own <html> and <body> because the root layout never mounted. Keep this
// dependency-free — DDS components are intentionally not used here because
// any failure in the root layout's providers (theme, application-context)
// could cascade into this fallback. `reset` is intentionally NOT wired to the
// retry button: if the root cause is deterministic (bad provider, env var),
// reset() loops back here. A hard reload at least re-pulls HTML/HMR state.
export default function GlobalError({ error }: GlobalErrorProps) {
  useEffect(() => {
    reportGlobalError(error);
  }, [error]);

  return (
    <html lang="en">
      <head>
        <title>Error - DIRECTV</title>
      </head>
      <body
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          margin: 0,
          padding: "48px 24px",
          backgroundColor: "#fff",
          color: "#102641",
          minHeight: "100vh"
        }}
      >
        <main
          style={{
            maxWidth: "640px",
            margin: "0 auto",
            textAlign: "center"
          }}
        >
          <div role="alert" aria-live="assertive">
            <h1
              style={{
                fontSize: "28px",
                margin: "0 0 16px"
              }}
            >
              Something went wrong
            </h1>
            <p
              style={{
                fontSize: "16px",
                margin: "0 0 24px"
              }}
            >
              We&apos;re working on it. Please try again.
            </p>
          </div>
          <button
            type="button"
            style={{
              backgroundColor: "#102641",
              color: "#fff",
              border: 0,
              borderRadius: "4px",
              padding: "12px 24px",
              fontSize: "16px",
              cursor: "pointer"
            }}
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        </main>
      </body>
    </html>
  );
}
